// "Apply for Fall / Spring" strip on property room cards (P1D-5532).
//
// The intakes, their move-in windows and copy live here and always win: once
// today is past an intake's windowEnd the strip is gone, whatever the CRM says.
// The CRM "Future Intake" page only decides, per intake, whether it is enabled
// and for which destination countries (s.base application_configs rows).

export type IntakeKey = "fall" | "spring";

export type IntakeSeason = "Fall" | "Spring";

export interface FutureIntake {
  key: IntakeKey;
  season: IntakeSeason;
  /** Two-digit year, used in "Spring'27". */
  yy: string;
  /** Four-digit year, used in "Spring 2027". */
  year: string;
  crmComponent: "apply_for_fall_experiment" | "apply_for_spring_experiment";
  /** First move-in day, YYYY-MM-DD. */
  windowStart: string;
  /** Last move-in day, YYYY-MM-DD. The strip stops after this date. */
  windowEnd: string;
}

// Keep in sync with FUTURE_INTAKE_SECTIONS in amber-crm (src/app/future-intake/constants.ts),
// which shows these ranges as info text only.
export const FUTURE_INTAKES: FutureIntake[] = [
  {
    key: "spring",
    season: "Spring",
    yy: "27",
    year: "2027",
    crmComponent: "apply_for_spring_experiment",
    windowStart: "2027-01-01",
    windowEnd: "2027-02-15",
  },
  {
    key: "fall",
    season: "Fall",
    yy: "27",
    year: "2027",
    crmComponent: "apply_for_fall_experiment",
    windowStart: "2027-08-01",
    windowEnd: "2027-09-30",
  },
];

export const FUTURE_INTAKE_CONFIG_PAGE = "property_page";

export interface IntakeToggle {
  enabled: boolean;
  countries: string[];
}

export type FutureIntakeConfigMap = Partial<Record<IntakeKey, IntakeToggle>>;

const ISO_COUNTRY = /^[A-Z]{2}$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Maps the public application_config rows to one toggle per intake. */
export const parseFutureIntakeConfig = (rows: any): FutureIntakeConfigMap => {
  const map: FutureIntakeConfigMap = {};
  if (!Array.isArray(rows)) return map;
  FUTURE_INTAKES.forEach((intake) => {
    const row = rows.find((r: any) => r?.component_name === intake.crmComponent);
    if (!row) return;
    const countries: string[] = (Array.isArray(row?.data?.countries) ? row.data.countries : [])
      .map((c: any) => String(c).trim().toUpperCase())
      .filter((c: string) => ISO_COUNTRY.test(c));
    map[intake.key] = { enabled: row.status === "active" && countries.length > 0, countries };
  });
  return map;
};

const pad = (n: number) => String(n).padStart(2, "0");

/** Local calendar date as YYYY-MM-DD (the user's day, not UTC). */
export const toISODate = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const getTodayISO = (): string => toISODate(new Date());

const MONTHS: Record<string, number> = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};
const DMY_DASHED = /^(\d{1,2})-(\d{1,2})-(\d{4})$/; // meta.available_from: "24-10-2026"
const D_MON_Y = /^(\d{1,2})\s+([A-Za-z]{3})[A-Za-z]*,?\s+(\d{4})$/; // "24 Oct, 2026"

const toISO = (y: number, m: number, d: number): string | null =>
  m >= 1 && m <= 12 && d >= 1 && d <= 31 ? `${y}-${pad(m)}-${pad(d)}` : null;

/**
 * Normalises a tenancy move-in value to YYYY-MM-DD, or null when it can't be read.
 * Parsed by hand, not with new Date(string), so every browser and the server
 * read "24 Oct, 2026" the same way.
 */
export const toMoveInISO = (value: any): string | null => {
  if (!value || typeof value !== "string") return null;
  const trimmed = value.trim();
  if (ISO_DATE.test(trimmed.slice(0, 10))) return trimmed.slice(0, 10);
  const dmy = trimmed.match(DMY_DASHED);
  if (dmy) return toISO(Number(dmy[3]), Number(dmy[2]), Number(dmy[1]));
  const dMonY = trimmed.match(D_MON_Y);
  if (dMonY) {
    const month = MONTHS[dMonY[2].toLowerCase()];
    return month ? toISO(Number(dMonY[3]), month, Number(dMonY[1])) : null;
  }
  return null;
};

/**
 * The intake the strip should advertise on this property, or null. Intakes are
 * tried in order of windowEnd, so when both are enabled for a country the one
 * ending first wins (one strip per room) and Fall takes over after Spring ends.
 */
export const getActiveIntake = ({
  config,
  countryCode,
  todayISO,
  intakes = FUTURE_INTAKES,
}: {
  config?: FutureIntakeConfigMap | null;
  countryCode?: string;
  todayISO: string;
  intakes?: FutureIntake[];
}): FutureIntake | null => {
  if (!config || !countryCode) return null;
  const country = countryCode.toUpperCase();
  const ordered = [...intakes].sort((a, b) => a.windowEnd.localeCompare(b.windowEnd));
  return (
    ordered.find((intake) => {
      if (todayISO > intake.windowEnd) return false;
      const toggle = config[intake.key];
      return !!toggle?.enabled && toggle.countries.includes(country);
    }) || null
  );
};

/**
 * Move-in matrix for an active intake (requirement change 2026-10-26).
 * A tenancy hides the strip when it moves in inside the window, or after the
 * window once today has reached the window. Move-ins before the window never
 * hide it. Example for Spring'27 (1 Jan – 15 Feb 2027):
 *
 *   today \ move-in   before 1 Jan   1 Jan – 15 Feb   after 15 Feb
 *   before 1 Jan      show           hide             show
 *   1 Jan – 15 Feb    show           hide             hide
 *   (after 15 Feb the intake is not active: getActiveIntake returns null)
 */
export const doesMoveInHideStrip = ({
  moveIn,
  intake,
  todayISO,
}: {
  moveIn: string;
  intake: FutureIntake;
  todayISO: string;
}): boolean => {
  if (moveIn < intake.windowStart) return false;
  if (moveIn <= intake.windowEnd) return true;
  return todayISO >= intake.windowStart;
};

// One key per season (the season word is translated per language); the year is
// interpolated as {year} ("2027") or {yy} ("27").
export const FUTURE_INTAKE_COPY_KEYS: Record<
  IntakeKey,
  {
    titleDesktop: string;
    titleMobile: string;
    cta: string;
    leadFormTitle: string;
  }
> = {
  fall: {
    titleDesktop: "propertypage.futureintake.fall.titledesktop",
    titleMobile: "propertypage.futureintake.fall.titlemobile",
    cta: "propertypage.futureintake.fall.cta",
    leadFormTitle: "leadform.future_intake.fall.title",
  },
  spring: {
    titleDesktop: "propertypage.futureintake.spring.titledesktop",
    titleMobile: "propertypage.futureintake.spring.titlemobile",
    cta: "propertypage.futureintake.spring.cta",
    leadFormTitle: "leadform.future_intake.spring.title",
  },
};

/** Interpolation values for the season-specific translation keys. */
export const getIntakeCopyParams = (intake: FutureIntake) => ({
  year: intake.year,
  yy: intake.yy,
});

/** "Spring'27" for the lead meta tag and analytics. */
export const getIntakeName = (intake: FutureIntake): string => `${intake.season}'${intake.yy}`;

/** Finds the intake back from its name, e.g. on the lead form. */
export const getIntakeByName = (name?: string): FutureIntake | null =>
  FUTURE_INTAKES.find((intake) => getIntakeName(intake) === name) || null;
